import {
  Edit,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextInput,
  BooleanInput,
  SelectArrayInput,
  FormDataConsumer,
  Toolbar,
  SaveButton,
  DeleteButton,
  AutocompleteInput,
} from "react-admin";
import { statesList, roleOptions, privilegedRoleIds } from "../../../lib/constants";
import { useAuth } from "../../../hooks/useAuth";
import { Typography, Grid } from "@mui/material";



const AgencyInput = () => {
  return (
    <FormDataConsumer>
      {({ formData }) => {
        if (formData.role !== "agency") {
          return;
        }
        return (
          <>
            <ReferenceInput source="agency" reference="agency">
              <AutocompleteInput fullWidth optionText="name" />
            </ReferenceInput>
          </>
        );
      }}
    </FormDataConsumer>
  );
};

const NotificationControls = ({ readonly }) => {
  return (
    <FormDataConsumer>
      {({ formData }) => {
        if (formData.role !== "admin") {
          return;
        }
        return (
          <>
            <BooleanInput
              label="Notify of new contract requests for all states"
              source="notifyAllContracts"
              disabled={readonly}
            />
            {!formData.notifyAllContracts && (
              <SelectArrayInput
                label="Notify of Contract Requests for these states"
                source="notifyContractStates"
                style={{ width: "90%" }}
                choices={statesList}
                disabled={readonly}
              />
            )}
            <BooleanInput
              label="Notify for new GA Contract requests"
              source="notifyGAContract"
              disabled={readonly}
            />
          </>
        );
      }}
    </FormDataConsumer>
  );
};

const ActionToolbar = ({ canEdit, showDelete }) => {
  if (!canEdit) {
    return null;
  }
  return (
    <Toolbar>
      <SaveButton />
      {showDelete && <DeleteButton />}
    </Toolbar>
  );
};

export const UserEdit = () => {
  const { user, isFullAdmin, isLiteAdmin, canDelete } = useAuth();
  // Full Applied GA admins get everything. Lite Admins (#73) may edit user
  // fields and assign only non-privileged roles, but cannot delete, cannot
  // re-assign agency, and cannot link an Agent record. Everyone else is
  // read-only.
  const fullAdmin = isFullAdmin(user);
  const canEdit = fullAdmin || isLiteAdmin(user);
  const editDisabled = !canEdit;
  const roleChoices = fullAdmin
    ? roleOptions
    : roleOptions.filter((r) => !privilegedRoleIds.includes(r.id));

  return (
    <Edit redirect="list">
      <SimpleForm
        warnWhenUnsavedChanges
        toolbar={<ActionToolbar canEdit={canEdit} showDelete={canDelete(user)} />}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextInput source="firstName" fullWidth disabled={editDisabled} />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="lastName" fullWidth disabled={editDisabled} />
              </Grid>
              <Grid item xs={6}>
                <TextInput
                  source="preferredName"
                  fullWidth
                  disabled={editDisabled}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="email" fullWidth disabled={editDisabled} />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <SelectInput
                  disabled={editDisabled}
                  source="role"
                  choices={roleChoices}
                />
              </Grid>

              {fullAdmin && (
                <Grid item xs={6}>
                  <AgencyInput />
                </Grid>
              )}
            </Grid>
            <NotificationControls readonly={editDisabled} />
            {fullAdmin && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ReferenceInput source="agent" reference="agent">
                    <AutocompleteInput
                      fullWidth
                      label="Linked Agent (CRM)"
                      optionText={(r) =>
                        r?.id ? `${r.npn} — ${r.firstName || ""} ${r.lastName || ""}`.trim() : ""
                      }
                    />
                  </ReferenceInput>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item xs={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">Contact Information</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextInput source="address" fullWidth disabled={editDisabled} />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="city" fullWidth disabled={editDisabled} />
              </Grid>
              <Grid item xs={6}>
                <SelectInput
                  source="state"
                  choices={statesList}
                  disabled={editDisabled}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="zip" disabled={editDisabled} />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="phone" fullWidth disabled={editDisabled} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SimpleForm>
    </Edit>
  );
};
export default UserEdit;
